export const MAX_SECTION_NAME = 100;
export const MAX_TOPIC_NAME = 100;
export const MAX_TOPIC_EXPLANATION = 10000;
export const MAX_VIDEO_SIZE_MB = 200;
export const MAX_IMAGE_SIZE_MB = 5;

export const NAME_PATTERN = /^[a-zA-Z0-9\s\-&,.'()]+$/;
export const TOPIC_NAME_PATTERN = /^[a-zA-Z0-9\s\-&,.'()?:]+$/;

export const MODULE_ACTIVITY_TREE = [
  {
    id: "MOD-CLI",
    name: "Client Management",
    activities: [
      { id: "ACT-CLI-01", name: "View Client Directory" },
      { id: "ACT-CLI-02", name: "Manage Client Licenses" },
    ],
  },
  {
    id: "MOD-HLP",
    name: "Help Manual",
    activities: [
      { id: "ACT-HLP-01", name: "Manage Help Manual" },
      { id: "ACT-HLP-02", name: "View Help Manual" },
      { id: "ACT-HLP-03", name: "Export Documentation" },
    ],
  },
  {
    id: "MOD-ROL",
    name: "Role Management",
    activities: [
      { id: "ACT-ROL-01", name: "View Roles & Permissions" },
      { id: "ACT-ROL-02", name: "Manage Role Assignments" },
    ],
  },
  {
    id: "MOD-ACT",
    name: "Activity Management",
    activities: [
      { id: "ACT-ACT-01", name: "Audit User Activities" },
      { id: "ACT-ACT-02", name: "Activity Log Export" },
    ],
  },
  {
    id: "MOD-FSM",
    name: "FS Masters",
    activities: [
      { id: "ACT-FSM-01", name: "Configure Field Service Settings" },
      { id: "ACT-FSM-02", name: "Dispatch & Territory Rules" },
    ],
  },
];

export const INITIAL_SECTIONS = [
  {
    id: "sec-1",
    name: "Getting Started with CMS",
    topics: [
      {
        id: "top-001",
        name: "System Overview and Navigation",
        explanation:
          "<p>Welcome to the Client Management System. This module introduces the core interface, header functions, and left-hand navigation.</p><p><strong>Key capabilities include:</strong></p><ol><li>Client lifecycle tracking</li><li>Role-based access control</li><li>Dynamic user manuals for enterprise documentation</li><li>License and subscription status controls.</li></ol>",
        video: {
          name: "cms_quick_overview.mp4",
          sizeLabel: "24.8 MB",
          ready: true,
          url: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
        },
        activityIds: { enterprise: ["ACT-CLI-01", "ACT-HLP-02"], site: ["ACT-HLP-02"] },
        lastUpdated: "2026-08-01",
      },
      {
        id: "top-002",
        name: "Managing User Access and Roles",
        explanation:
          "<p>Learn how to assign roles, configure permissions, and audit user access across enterprise and site scopes.</p>",
        video: null,
        activityIds: { enterprise: ["ACT-ROL-01", "ACT-ROL-02"], site: [] },
        lastUpdated: "2026-07-28",
      },
    ],
  },
  {
    id: "sec-2",
    name: "Client Operations & Provisioning",
    topics: [
      {
        id: "top-003",
        name: "Adding and Onboarding New Clients",
        explanation:
          "<p>Step-by-step instructions for creating a new enterprise client, assigning licenses, and activating modules.</p>",
        video: null,
        activityIds: { enterprise: ["ACT-CLI-01", "ACT-CLI-02"], site: ["ACT-CLI-01"] },
        lastUpdated: "2026-07-20",
      },
      {
        id: "top-004",
        name: "Managing Client Licenses & Status",
        explanation:
          "<p>Update license counts, subscription dates, and client activation status from the client directory.</p>",
        video: null,
        activityIds: { enterprise: ["ACT-CLI-02"], site: ["ACT-CLI-02"] },
        lastUpdated: "2026-07-15",
      },
    ],
  },
  {
    id: "sec-3",
    name: "Field Service Masters Configuration",
    topics: [
      {
        id: "top-005",
        name: "Configuring Diets, Recipients & Occasions",
        explanation:
          "<p>Configure FS master data used across meal services, including diets, recipients, occasions, and allergens.</p>",
        video: null,
        activityIds: { enterprise: ["ACT-FSM-01"], site: [] },
        lastUpdated: "2026-07-10",
      },
    ],
  },
];

export const generateId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const stripHtml = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

export const emptyActivityMapping = () => ({ enterprise: [], site: [] });

export const normalizeActivityMapping = (value) => {
  if (!value) return emptyActivityMapping();
  if (Array.isArray(value)) {
    return { enterprise: [...value], site: [] };
  }
  return {
    enterprise: Array.isArray(value.enterprise) ? [...value.enterprise] : [],
    site: Array.isArray(value.site) ? [...value.site] : [],
  };
};

export const mappingSelectedCount = (value, scope) => {
  const mapping = normalizeActivityMapping(value);
  if (scope === "enterprise" || scope === "site") {
    return mapping[scope].length;
  }
  return mapping.enterprise.length + mapping.site.length;
};

export const flattenMappedActivityIds = (value) => {
  const mapping = normalizeActivityMapping(value);
  return [...new Set([...mapping.enterprise, ...mapping.site])];
};
